import { map, max } from "lodash/fp";

import { Stack, Box, Typography } from "@mui/material";

import { TeamsListData } from "./types";
import { getPropertyToSortBy } from "./utils";

const PolicyBar = ({
  teams,
  workspaceAverages,
  currentTab,
  metric,
}: TeamsListData) => {
  const propertyToSortBy = getPropertyToSortBy(currentTab, metric);

  // add 1 to the max value when viewing my time to account for rounding in small ranges
  const maxValue =
    metric === "percent"
      ? 100
      : Math.ceil(
          max(map(propertyToSortBy, [...teams, workspaceAverages])) ?? 0
        ) + 1;

  const policyThreshold = workspaceAverages[propertyToSortBy];
  const policyThresholdPercent = (policyThreshold / maxValue) * 100;

  const workspaceAveragePercentOnLine =
    (workspaceAverages[propertyToSortBy] / maxValue) * 100;

  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ pl: 1 }}>
      <Stack
        direction="row"
        alignItems="center"
        sx={{ width: "100%", position: "relative" }}
      >
        <Box
          sx={{
            width: `${policyThresholdPercent}%`,
            borderRadius:
              policyThresholdPercent >= 100 ? "8px" : "8px 0px 0px 8px",
            bgcolor: "shades.meadow.100",
            height: "8px",
          }}
        />
        <Box
          sx={{
            width: `${100 - policyThresholdPercent}%`,
            borderRadius:
              policyThresholdPercent <= 0 ? "8px" : "0px 8px 8px 0px",
            bgcolor: "shades.negative.100",
            height: "8px",
          }}
        />
        {teams.map((team) => {
          const isInPolicy = team[propertyToSortBy] <= policyThreshold;
          const percentOnLine = (team[propertyToSortBy] / maxValue) * 100;

          return (
            <Box
              key={team.id}
              sx={{
                borderRadius: "50%",
                position: "absolute",
                width: "12px",
                height: "12px",
                backgroundColor: isInPolicy
                  ? "scores.positive"
                  : "scores.negative",
                opacity: "0.75",
                ml: `calc(${percentOnLine}% - 6px)`, // 6px to visually center dot, offset is 1/2 width of dot
              }}
            />
          );
        })}
        {/* Workspace average dot */}
        <Box
          sx={{
            borderRadius: "50%",
            position: "absolute",
            width: "12px",
            height: "12px",
            backgroundColor: "shades.primary.500",
            ml: `calc(${workspaceAveragePercentOnLine}% - 6px)`, // 6px to visually center dot, offset is 1/2 width of dot
          }}
        />
      </Stack>
      <Typography variant="label1" sx={{ color: "shades.neutral.300" }}>
        {maxValue}
        {metric === "percent" ? "%" : "m"}
      </Typography>
    </Stack>
  );
};

export default PolicyBar;
